import { Component } from '@theme/component';

/** Keep the brand and swatch together, with a full-width colour line only when needed. */
class TampinoColourTitle extends Component {
  #observer;
  connectedCallback() {
    super.connectedCallback();
    const brand = this.querySelector('.tampino-colour-card__title-brand');
    const name = this.querySelector('.tampino-colour-card__colour-name');
    const separator = this.querySelector('.tampino-colour-card__title-separator');
    if (!brand || !name || !separator) return;
    const fullWidth = (title) => {
      const titleBrand = title.querySelector('.tampino-colour-card__title-brand');
      const titleName = title.querySelector('.tampino-colour-card__colour-name');
      const titleSeparator = title.querySelector('.tampino-colour-card__title-separator');
      // The hidden separator stays measurable so a wrapped title cannot oscillate.
      return titleBrand.getBoundingClientRect().width + titleName.getBoundingClientRect().width +
        (title.hasAttribute('data-wrapped') ? titleSeparator.getBoundingClientRect().width : 0);
    };
    const layout = () => {
      if (!this.isConnected || !this.clientWidth) return;
      const grid = this.closest('.tampino-colours__grid');
      const rowTop = this.closest('.tampino-colour-card')?.offsetTop;
      // Use layout offsets rather than transformed bounds so card hover cannot split a row.
      const row = grid ? [...grid.querySelectorAll('tampino-colour-title')].filter(title =>
        title.clientWidth && Math.abs(title.closest('.tampino-colour-card').offsetTop - rowTop) < 1) : [this];
      const wrapped = row.some(title => fullWidth(title) > title.getBoundingClientRect().width - .25);
      for (const title of row) title.toggleAttribute('data-wrapped', wrapped);
    };
    this.#observer = new ResizeObserver(layout);
    for (const element of [this, brand, name]) this.#observer.observe(element);
    layout();
  }
  disconnectedCallback() {
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }
}

/** Shared native-details motion. Without the module, details remains fully usable. */
class TampinoDisclosure extends Component {
  #controller;
  #animation;
  #expanded;
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const details = this.querySelector('details');
    const summary = details?.querySelector('summary');
    if (!details || !summary) return;
    this.#expanded = details.open;
    summary.addEventListener('click', this.#toggle, { signal: this.#controller.signal });
  }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#animation?.cancel();
    super.disconnectedCallback();
  }
  #toggle = (event) => {
    const details = this.querySelector('details');
    const summary = details?.querySelector('summary');
    if (!details || !summary) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#animation?.cancel();
      details.style.overflow = '';
      this.#expanded = !details.open;
      return;
    }
    event.preventDefault();
    const start = details.getBoundingClientRect().height;
    this.#animation?.cancel();
    this.#expanded = !this.#expanded;
    details.open = true;
    const end = this.#expanded ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height;
    details.style.overflow = 'hidden';
    const animation = details.animate({ height: [`${start}px`, `${end}px`] }, { duration: 240, easing: 'ease-in-out' });
    this.#animation = animation;
    animation.onfinish = () => {
      details.open = this.#expanded;
      details.style.overflow = '';
      this.#animation = undefined;
    };
  };
}

/** Native selects filter existing product links, without false pointer focus. */
class TampinoCatalogue extends Component {
  #controller;
  #tiles;
  #menus = [];
  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    for (const menu of this.#menus) {
      menu.trigger.remove(); menu.panel.remove(); menu.select.hidden = false;
    }
    this.#menus = [];
    this.#controller = new AbortController();
    const grid = this.querySelector('[data-catalogue-grid]');
    if (!grid) return;
    this.#tiles = [...grid.querySelectorAll('.tampino-colour-card')];
    const { signal } = this.#controller;
    this.addEventListener('change', this.#update, { signal });
    this.querySelectorAll('[data-catalogue-filter]').forEach(select => this.#enhanceSelect(select, signal));
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.#closeMenus();
    }, { signal });
    this.addEventListener('pointerdown', event => {
      if (event.target instanceof HTMLSelectElement) event.target.setAttribute('data-tampino-pointer-focus', '');
    }, { signal });
    document.addEventListener('keydown', () => {
      this.querySelectorAll('[data-tampino-pointer-focus]').forEach(select => select.removeAttribute('data-tampino-pointer-focus'));
    }, { signal, capture: true });
  }
  disconnectedCallback() {
    this.#controller?.abort();
    for (const menu of this.#menus) {
      menu.trigger.remove(); menu.panel.remove(); menu.select.hidden = false;
    }
    this.#menus = [];
    super.disconnectedCallback();
  }
  #closeMenus(except) {
    for (const menu of this.#menus) {
      if (menu === except) continue;
      menu.panel.hidden = true;
      menu.trigger.setAttribute('aria-expanded', 'false');
    }
  }
  #enhanceSelect(select, signal) {
    if (!(select instanceof HTMLSelectElement) || !select.options.length) return;
    const field = select.closest('.tampino-select-field');
    const label = field?.querySelector('label');
    if (!field || !label) return;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'tampino-catalogue-menu__trigger';
    trigger.id = `${select.id}-trigger`;
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', label.textContent.trim());
    const value = document.createElement('span');
    value.className = 'tampino-catalogue-menu__value';
    const caret = document.createElement('span');
    caret.className = 'tampino-catalogue-menu__caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML = '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 5.5 7 9.5 11 5.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    trigger.append(value, caret);
    const panel = document.createElement('div');
    panel.className = 'tampino-catalogue-menu__choices';
    panel.id = `${select.id}-choices`;
    panel.role = 'listbox';
    panel.setAttribute('aria-label', label.textContent.trim());
    panel.hidden = true;
    trigger.setAttribute('aria-controls', panel.id);
    const choices = [...select.options].map(option => {
      const choice = document.createElement('button');
      choice.type = 'button';
      choice.className = 'tampino-catalogue-menu__choice';
      choice.role = 'option';
      choice.dataset.value = option.value;
      choice.textContent = option.textContent;
      panel.append(choice);
      return choice;
    });
    const menu = { trigger, panel, choices, select, value };
    const sync = () => {
      value.textContent = select.selectedOptions[0]?.textContent || '';
      choices.forEach(choice => choice.setAttribute('aria-selected', String(choice.dataset.value === select.value)));
    };
    const open = () => {
      this.#closeMenus(menu);
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      choices.find(choice => choice.dataset.value === select.value)?.focus();
    };
    trigger.addEventListener('click', () => panel.hidden ? open() : this.#closeMenus(), { signal });
    label.htmlFor = trigger.id;
    label.addEventListener('click', event => { event.preventDefault(); trigger.click(); }, { signal });
    panel.addEventListener('click', event => {
      const choice = event.target.closest('[data-value]');
      if (!choice) return;
      select.value = choice.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
      this.#closeMenus();
      trigger.focus();
    }, { signal });
    field.addEventListener('keydown', event => {
      if (!field.contains(event.target)) return;
      if (event.key === 'Escape' && !panel.hidden) {
        event.preventDefault(); this.#closeMenus(); trigger.focus(); return;
      }
      if (!['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
      event.preventDefault();
      if (panel.hidden) { open(); return; }
      const index = choices.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 : (index + (event.key === 'ArrowUp' ? -1 : 1) + choices.length) % choices.length;
      choices[next]?.focus();
    }, { signal });
    field.addEventListener('focusout', event => {
      if (!field.contains(event.relatedTarget)) this.#closeMenus();
    }, { signal });
    field.append(trigger, panel);
    select.hidden = true;
    this.#menus.push(menu);
    select.addEventListener('change', sync, { signal });
    sync();
  }
  #update = () => {
    const grid = this.querySelector('[data-catalogue-grid]');
    const filter = this.querySelector('[data-catalogue-filter]');
    if (!grid || !this.#tiles) return;
    const ordered = [...this.#tiles];
    ordered.forEach(tile => { tile.hidden = !!filter && filter.value !== 'all' && tile.dataset.kind !== filter.value; grid.append(tile); });
  };
}

/** Narrow viewports advance in one direction, including the final-to-first step. */
class TampinoTrustBar extends Component {
  #controller;
  #index = 0;
  #timer;
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const track = this.querySelector('.tampino-trust-facts');
    if (!track || track.children.length < 2) return;
    const originals = track.children.length;
    const clone = track.firstElementChild.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a,button').forEach(link => link.tabIndex = -1);
    track.append(clone);
    this.setAttribute('data-enhanced', '');
    const narrow = window.matchMedia('(max-width: 999px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reset = () => {
      track.style.transition = 'none';
      track.style.transform = 'translateX(0)';
      this.#index = 0;
    };
    const schedule = () => {
      clearTimeout(this.#timer);
      if (!narrow.matches || reduced.matches || document.hidden || this.contains(document.activeElement)) return;
      this.#timer = setTimeout(() => {
        this.#index += 1;
        track.style.transition = 'transform .42s cubic-bezier(.22,.65,.3,1)';
        track.style.transform = `translateX(-${this.#index * 100 / (originals + 1)}%)`;
        schedule();
      }, 5000);
    };
    track.addEventListener('transitionend', event => {
      if (event.propertyName !== 'transform' || this.#index !== originals) return;
      reset();
    }, { signal: this.#controller.signal });
    this.addEventListener('focusin', schedule, { signal: this.#controller.signal });
    this.addEventListener('focusout', schedule, { signal: this.#controller.signal });
    document.addEventListener('visibilitychange', schedule, { signal: this.#controller.signal });
    narrow.addEventListener('change', () => { reset(); schedule(); }, { signal: this.#controller.signal });
    reduced.addEventListener('change', () => { reset(); schedule(); }, { signal: this.#controller.signal });
    schedule();
  }
  disconnectedCallback() { clearTimeout(this.#timer); this.#controller?.abort(); super.disconnectedCallback(); }
}
for (const [name, component] of [['tampino-colour-title', TampinoColourTitle], ['tampino-disclosure', TampinoDisclosure], ['tampino-catalogue-component', TampinoCatalogue], ['tampino-trust-bar', TampinoTrustBar]]) {
  if (!customElements.get(name)) customElements.define(name, component);
}

/** One dot hop per pointer entry; a logo click stays quiet through navigation until the pointer leaves. */
let logoRequiresLeave = false;
try { logoRequiresLeave = window.sessionStorage.getItem('tampino-logo-await-leave') === '1'; } catch (_) {}
const releaseLogoHover = () => {
  logoRequiresLeave = false;
  try { window.sessionStorage.removeItem('tampino-logo-await-leave'); } catch (_) {}
};
document.addEventListener('pointerover', event => {
  const logo = event.target.closest?.('.header-logo');
  if (!logo || logo.contains(event.relatedTarget) || event.pointerType !== 'mouse') return;
  if (logoRequiresLeave || !window.matchMedia('(hover: hover) and (pointer: fine)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  logo.querySelector('.tampino-logo-dot')?.animate([
    { transform: 'translateY(0)', offset: 0 },
    { transform: 'translateY(-8px)', offset: .45 },
    { transform: 'translateY(0)', offset: 1 },
  ], { duration: 600, easing: 'ease-in-out' });
});
document.addEventListener('pointerout', event => {
  const logo = event.target.closest?.('.header-logo');
  if (logo && !logo.contains(event.relatedTarget)) releaseLogoHover();
});
document.addEventListener('pointermove', event => {
  if (logoRequiresLeave && !event.target.closest?.('.header-logo')) releaseLogoHover();
});
document.addEventListener('click', event => {
  if (!event.target.closest?.('.header-logo') || !event.detail || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  logoRequiresLeave = true;
  try { window.sessionStorage.setItem('tampino-logo-await-leave', '1'); } catch (_) {}
});

/** Reveal content as it enters the viewport; HTML stays visible without JS or motion support. */
if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !document.documentElement.classList.contains('shopify-design-mode')) {
  const main = document.getElementById('MainContent');
  if (main) {
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -10% 0px' });
    const grids = '.tampino-content__grid,.tampino-colours__grid,.tampino-guides__grid,.tampino-testimonials__grid,.tampino-faq-grid';
    const prepare = root => {
      const targets = [];
      for (const section of root.querySelectorAll('.tampino-section,.tampino-contact')) {
        for (const child of section.children) {
          if (child.matches(grids)) targets.push(...child.children);
          else if (!child.matches('script,style')) targets.push(child);
        }
      }
      for (const target of targets) {
        if (target.hasAttribute('data-tampino-reveal')) continue;
        const bounds = target.getBoundingClientRect();
        target.setAttribute('data-tampino-reveal', '');
        // Never hide content that was already painted anywhere in the viewport.
        if (bounds.top < innerHeight || bounds.bottom < 0) target.classList.add('is-visible');
        else observer.observe(target);
      }
      if (targets.length) document.documentElement.classList.add('tampino-reveal-ready');
    };
    prepare(main);
    document.addEventListener('shopify:section:load', event => prepare(event.target));
    main.addEventListener('focusin', event => event.target.closest('[data-tampino-reveal]')?.classList.add('is-visible'));
  }
}
